import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  CheckCircle,
  Building2,
  Upload,
  Plus,
  Trash2,
  Pencil,
  Image as ImageIcon
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import SearchableSelect from '../ui/SearchableSelect';
import LocationAutocomplete from '../ui/LocationAutocomplete';

const createEmptyAddressDraft = () => ({
  POBOX: '',
  Address: '',
  ContactNo: '',
  StoreNo: '',
  IsDefault: false,
  IsActive: true,
  LocationId: '',
  EmirateId: '',
  Location: { Name: '' },
  LocationUrl: '',
});

const AddClient = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emirates, setEmirates] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [addressDraft, setAddressDraft] = useState(() => createEmptyAddressDraft());
  /** `null` = add new address; otherwise global index in `client.ClientAddresses` being edited. */
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);

  // Client form data
  const [client, setClient] = useState({
    Id: 0,
    Name: '',
    ClientNo: '',
    MobileNo1: '',
    MobileNo2: '',
    TelNo: '',
    Company: '',
    DeliveryCharges: 0,
    EmailId: '',
    Remarks: '',
    Photo: '',
    BankName: '',
    BankAccNo: '',
    BankAccName: '',
    PayMode: 'CASH',
    ResponsibleById: '',
    IsArabic: false,
    IsZeroCharge: false,
    Date: new Date().toISOString().split('T')[0],
    ClientAddresses: []
  });

  useEffect(() => {
    const initialize = async () => {
      await loadDropdowns();
      if (clientId && parseInt(clientId) > 0) {
        await loadClient(parseInt(clientId));
      } else {
        // Get next client number
        try {
          const response = await MainService.GetNextClientNo();
          if (response) {
            setClient(prev => ({ ...prev, ClientNo: response }));
          }
        } catch (err) {
          console.error('Error getting next client number:', err);
        }
      }
    };
    initialize();
  }, [clientId]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadDropdowns = async () => {
    try {
      const [emiratesRes, driversRes] = await Promise.all([
        MainService.EmiratesInDropDown(),
        MainService.EmployeesInDropDown()
      ]);
      setEmirates(emiratesRes || []);
      setDrivers(driversRes || []);
    } catch (err) {
      console.error('Error loading dropdowns:', err);
      setError('Failed to load dropdown data');
    }
  };

  const loadClient = async (id) => {
    try {
      setLoading(true);
      const response = await MainService.ClientById(id);
      if (response) {
        setClient({
          ...response,
          Date: response.Date ? new Date(response.Date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          ClientAddresses: response.ClientAddresses || [],
          DeliveryCharges: response.DeliveryCharges || 0,
          IsArabic: response.IsArabic || false,
          IsZeroCharge: response.IsZeroCharge || false
        });
        
        if (response.Photo) {
          const photoUrl = response.Photo && response.Photo.startsWith('http') 
            ? response.Photo 
            : `${getApiBaseUrl()}${response.Photo?.startsWith('/') ? response.Photo.slice(1) : response.Photo}`;
          setPhotoPreview(photoUrl);
        }
        setAddressDraft(createEmptyAddressDraft());
        setEditingAddressIndex(null);
      }
    } catch (err) {
      console.error('Error loading client:', err);
      setError('Failed to load client data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setClient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setClient(prev => ({ ...prev, Photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const ensureAtLeastOneDefault = (addresses) => {
    const next = [...addresses];
    const activeIdx = next.map((a, i) => (a.IsActive ? i : -1)).filter((i) => i >= 0);
    if (activeIdx.length === 0) return next;
    if (!activeIdx.some((i) => next[i].IsDefault)) {
      const first = activeIdx[0];
      next[first] = { ...next[first], IsDefault: true };
    }
    return next;
  };

  const commitAddressFromDraft = () => {
    if (!addressDraft.EmirateId || !addressDraft.LocationId || !addressDraft.Address?.trim()) {
      setError('Emirate, Location, and Address are required');
      return;
    }
    setError('');

    if (editingAddressIndex !== null) {
      const idx = editingAddressIndex;
      setClient((prev) => {
        if (idx < 0 || idx >= prev.ClientAddresses.length) return prev;
        const prevRow = prev.ClientAddresses[idx];
        if (!prevRow?.IsActive) return prev;
        const updated = {
          ...prevRow,
          POBOX: addressDraft.POBOX || '',
          Address: addressDraft.Address || '',
          ContactNo: addressDraft.ContactNo || '',
          StoreNo: addressDraft.StoreNo || '',
          EmirateId: addressDraft.EmirateId,
          LocationId: addressDraft.LocationId,
          Location: addressDraft.Location?.Name ? { Name: addressDraft.Location.Name } : { Name: '' },
          LocationUrl: addressDraft.LocationUrl || '',
          IsDefault: !!addressDraft.IsDefault,
          IsActive: true,
        };
        let addresses = [...prev.ClientAddresses];
        if (updated.IsDefault) {
          addresses = addresses.map((a, i) =>
            i === idx ? updated : a.IsActive ? { ...a, IsDefault: false } : a
          );
        } else {
          addresses[idx] = updated;
        }
        addresses = ensureAtLeastOneDefault(addresses);
        return { ...prev, ClientAddresses: addresses };
      });
      setEditingAddressIndex(null);
      setAddressDraft(createEmptyAddressDraft());
      return;
    }

    const activeBefore = client.ClientAddresses.filter((a) => a.IsActive);
    const isFirst = activeBefore.length === 0;
    const newRow = {
      ...addressDraft,
      IsActive: true,
      IsDefault: isFirst ? true : !!addressDraft.IsDefault,
    };
    setClient((prev) => {
      let addresses = [...prev.ClientAddresses];
      if (newRow.IsDefault) {
        addresses = addresses.map((a) => (a.IsActive ? { ...a, IsDefault: false } : a));
      }
      addresses.push(newRow);
      addresses = ensureAtLeastOneDefault(addresses);
      return { ...prev, ClientAddresses: addresses };
    });
    setAddressDraft(createEmptyAddressDraft());
  };

  const startEditAddress = (globalIndex) => {
    const row = client.ClientAddresses[globalIndex];
    if (!row?.IsActive) return;
    setAddressDraft({
      POBOX: row.POBOX || '',
      Address: row.Address || '',
      ContactNo: row.ContactNo || '',
      StoreNo: row.StoreNo || '',
      IsDefault: !!row.IsDefault,
      IsActive: true,
      LocationId: row.LocationId || '',
      EmirateId: row.EmirateId || '',
      Location: row.Location?.Name ? { Name: row.Location.Name } : { Name: '' },
      LocationUrl: row.LocationUrl || '',
    });
    setEditingAddressIndex(globalIndex);
    setError('');
  };

  const cancelAddressEdit = () => {
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddressDraft());
  };

  const removeAddress = (index) => {
    setEditingAddressIndex(null);
    setAddressDraft(createEmptyAddressDraft());
    setClient((prev) => {
      const addresses = [...prev.ClientAddresses];
      addresses[index] = { ...addresses[index], IsActive: false };
      return {
        ...prev,
        ClientAddresses: ensureAtLeastOneDefault(addresses),
      };
    });
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const deleteWithConfirm = async () => {
    try {
      setLoading(true);
      const response = await MainService.DeleteClient(client.Id);
      
      if (response === 'Success' || response === true) {
        setSuccess('Client deleted successfully');
        setTimeout(() => {
          navigate('/home/accounts/clients');
        }, 1500);
      } else {
        setError(response || 'Failed to delete client');
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      setError('Failed to delete client');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!client.Name || client.Name.trim() === '') {
      setError('Enter Name');
      return;
    }
    if (!client.MobileNo1 || client.MobileNo1.trim() === '') {
      setError('Enter Mobile Number');
      return;
    }
    if (client.DeliveryCharges === 0 && !client.IsZeroCharge) {
      setError('Enter Delivery Charge');
      return;
    }

    const activeAddresses = client.ClientAddresses.filter(a => a.IsActive);
    if (activeAddresses.length === 0) {
      setError('Add at least one address');
      return;
    }

    let addressInvalid = false;
    let defaultFound = false;
    let defaultCount = 0;

    activeAddresses.forEach(addr => {
      if (addr.IsDefault) {
        defaultFound = true;
        defaultCount++;
      }
      if (!addr.EmirateId || !addr.LocationId || !addr.Address || addr.Address.trim() === '') {
        addressInvalid = true;
      }
    });

    if (addressInvalid) {
      setError('Invalid Address Found | Emirate, Location, Address Compulsory');
      return;
    }

    if (!defaultFound) {
      setError('Make One Address As Default');
      return;
    }

    if (defaultCount !== 1) {
      setError('Make One Address As Default');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.SaveClient(client);
      
      if (response === 'Success' || response === true) {
        setSuccess('Client saved successfully');
        setTimeout(() => {
          navigate('/home/accounts/clients');
        }, 1500);
      } else {
        setError(response || 'Failed to save client');
      }
    } catch (err) {
      console.error('Error saving client:', err);
      setError('Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setClient({
      Id: 0,
      Name: '',
      ClientNo: '',
      MobileNo1: '',
      MobileNo2: '',
      TelNo: '',
      Company: '',
      DeliveryCharges: 0,
      EmailId: '',
      Remarks: '',
      Photo: '',
      BankName: '',
      BankAccNo: '',
      BankAccName: '',
      PayMode: 'CASH',
      ResponsibleById: '',
      IsArabic: false,
      IsZeroCharge: false,
      Date: new Date().toISOString().split('T')[0],
      ClientAddresses: []
    });
    setPhotoPreview(null);
    setPhotoFile(null);
    setAddressDraft(createEmptyAddressDraft());
    setEditingAddressIndex(null);
  };

  const setActiveAddressDefault = (globalIndex) => {
    setClient((prev) => ({
      ...prev,
      ClientAddresses: prev.ClientAddresses.map((a, idx) =>
        !a.IsActive ? a : { ...a, IsDefault: idx === globalIndex }
      ),
    }));
  };

  const getEmirateName = (id) => {
    const e = emirates.find((x) => x.Id === id);
    return e ? e.Name || e.Number || '—' : '—';
  };

  const tabs = [
    { id: 0, label: 'Basic' },
    { id: 1, label: 'Payment' },
    { id: 2, label: 'Address' }
  ];

  const activeAddresses = client.ClientAddresses.filter(a => a.IsActive);

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-1 border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <Building2 className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">
                {clientId && parseInt(clientId) > 0 ? 'Edit Client' : 'Add Client'} {client.ClientNo && `| ${client.ClientNo}`}
              </h1>
            </div>
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {loading && (
                <div className="w-6 h-6 border-2 border-[#46955A] border-t-transparent rounded-full animate-spin"></div>
              )}
              {client.Id > 0 && (
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
              <button
                onClick={handleReset}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] hover:border-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 ml-8">
            {clientId && parseInt(clientId) > 0 
              ? 'Update client information and details' 
              : 'Create new client and manage their information'}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium text-sm transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-[#46955A] border-b-2 border-[#46955A]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Basic Tab */}
        {activeTab === 0 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Photo Section */}
              <div className="lg:col-span-1">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer block">
                    <div className="w-full aspect-square max-w-xs mx-auto rounded-lg overflow-hidden border-2 border-gray-200 flex items-center justify-center bg-gray-50 relative group">
                      {photoPreview ? (
                        <>
                          <div 
                            className="absolute inset-0 bg-cover bg-center filter blur-md opacity-50"
                            style={{ backgroundImage: `url(${photoPreview})` }}
                          />
                          <img
                            src={photoPreview}
                            alt="Client"
                            className="relative z-10 w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center z-20">
                            <Upload className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </>
                      ) : (
                        <ImageIcon className="w-12 h-12 text-gray-400" />
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Form Fields */}
              <div className="lg:col-span-3 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={client.Name}
                      onChange={(e) => handleInputChange('Name', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Mobile No1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={client.MobileNo1}
                      onChange={(e) => handleInputChange('MobileNo1', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Mobile No2</label>
                    <input
                      type="text"
                      value={client.MobileNo2}
                      onChange={(e) => handleInputChange('MobileNo2', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Tel No</label>
                    <input
                      type="text"
                      value={client.TelNo}
                      onChange={(e) => handleInputChange('TelNo', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      value={client.EmailId}
                      onChange={(e) => handleInputChange('EmailId', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Company</label>
                    <input
                      type="text"
                      value={client.Company}
                      onChange={(e) => handleInputChange('Company', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Remarks</label>
                    <input
                      type="text"
                      value={client.Remarks}
                      onChange={(e) => handleInputChange('Remarks', e.target.value)}
                      className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Responsible By</label>
                    <SearchableSelect
                      value={client.ResponsibleById}
                      onChange={(value) => handleInputChange('ResponsibleById', value)}
                      options={drivers}
                      placeholder="Select Responsible By"
                    />
                  </div>
                  <div className="space-y-2 flex items-end">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={client.IsArabic || false}
                          onChange={(e) => handleInputChange('IsArabic', e.target.checked)}
                          className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                        />
                        <span className="text-sm font-medium text-gray-700">Arabic?</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={client.IsZeroCharge || false}
                          onChange={(e) => handleInputChange('IsZeroCharge', e.target.checked)}
                          className="w-4 h-4 text-[#46955A] border-gray-300 rounded focus:ring-[#46955A]"
                        />
                        <span className="text-sm font-medium text-gray-700">Free?</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Tab */}
        {activeTab === 1 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                <input
                  type="text"
                  value={client.BankName}
                  onChange={(e) => handleInputChange('BankName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Bank Acc No</label>
                <input
                  type="text"
                  value={client.BankAccNo}
                  onChange={(e) => handleInputChange('BankAccNo', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Bank Acc Name</label>
                <input
                  type="text"
                  value={client.BankAccName}
                  onChange={(e) => handleInputChange('BankAccName', e.target.value)}
                  className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Payment Mode</label>
                <SearchableSelect
                  value={client.PayMode}
                  onChange={(value) => handleInputChange('PayMode', value)}
                  options={[
                    { Id: 'CASH', Name: 'CASH' },
                    { Id: 'BANK', Name: 'BANK' }
                  ]}
                  placeholder="Select Payment Mode"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Delivery Charges <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={client.DeliveryCharges}
                  onChange={(e) => handleInputChange('DeliveryCharges', parseFloat(e.target.value) || 0)}
                  disabled={client.IsZeroCharge}
                  className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${
                    client.IsZeroCharge ? 'bg-gray-50 cursor-not-allowed opacity-60' : ''
                  }`}
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Address Tab — form on top (add or edit), addresses in table below */}
        {activeTab === 2 && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {editingAddressIndex !== null ? 'Edit address' : 'Add address'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {editingAddressIndex !== null ? (
                  <>
                    Update the fields below, then <span className="font-medium text-gray-700">Save changes</span>. Or{' '}
                    <button type="button" onClick={cancelAddressEdit} className="font-medium text-[#46955A] hover:underline">
                      cancel
                    </button>{' '}
                    to discard edits.
                  </>
                ) : (
                  <>
                    Enter details, then use <span className="font-medium text-gray-700">Add to list</span> to append. Use{' '}
                    <span className="font-medium text-gray-700">Edit</span> on a row below to change an existing address.
                  </>
                )}
              </p>
              <div
                className={`mt-4 space-y-4 rounded-lg border bg-gray-50/40 p-4 md:p-5 ${
                  editingAddressIndex !== null ? 'border-[#46955A] ring-1 ring-[#46955A]/25' : 'border-gray-200'
                }`}
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <SearchableSelect
                    value={addressDraft.EmirateId}
                    onChange={(value) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        EmirateId: value,
                        LocationId: '',
                        Location: { Name: '' },
                      }))
                    }
                    options={emirates}
                    placeholder="Select Emirate"
                    required
                    label="Emirate"
                  />
                  <LocationAutocomplete
                    value={addressDraft.Location}
                    onChange={(location) =>
                      setAddressDraft((prev) => ({
                        ...prev,
                        LocationId: location?.Id || '',
                        Location: { Name: location?.Name || '' },
                      }))
                    }
                    emirateId={addressDraft.EmirateId}
                    placeholder="Search location..."
                    disabled={!addressDraft.EmirateId}
                    required
                    label="Location"
                  />
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Contact No</label>
                    <input
                      type="text"
                      value={addressDraft.ContactNo || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, ContactNo: e.target.value }))}
                      className="w-full border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col justify-end gap-1 pb-2">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!addressDraft.IsDefault}
                        disabled={editingAddressIndex === null && activeAddresses.length === 0}
                        onChange={(e) => setAddressDraft((prev) => ({ ...prev, IsDefault: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-[#46955A] focus:ring-[#46955A] disabled:opacity-50"
                      />
                      <span className="text-sm font-medium text-gray-700">Set as default</span>
                    </label>
                    {editingAddressIndex === null && activeAddresses.length === 0 ? (
                      <p className="text-xs text-gray-500">The first address is always the default.</p>
                    ) : null}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
                  <div className="space-y-2 md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={2}
                      value={addressDraft.Address || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, Address: e.target.value }))}
                      className="w-full resize-none border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Location URL</label>
                    <input
                      type="text"
                      value={addressDraft.LocationUrl || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, LocationUrl: e.target.value }))}
                      className="w-full border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">PO Box</label>
                    <input
                      type="text"
                      value={addressDraft.POBOX || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, POBOX: e.target.value }))}
                      className="w-full border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Store No</label>
                    <input
                      type="text"
                      value={addressDraft.StoreNo || ''}
                      onChange={(e) => setAddressDraft((prev) => ({ ...prev, StoreNo: e.target.value }))}
                      className="w-full border-0 border-b-2 border-gray-300 bg-transparent px-4 py-2 focus:border-[#46955A] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4">
                  {editingAddressIndex !== null && (
                    <button
                      type="button"
                      onClick={cancelAddressEdit}
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={commitAddressFromDraft}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#46955A] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#3d7d4f]"
                  >
                    {editingAddressIndex !== null ? (
                      <>
                        <Pencil className="h-4 w-4" />
                        Save changes
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4" />
                        Add to list
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900">Address list</h3>
              <p className="mt-1 text-sm text-gray-500">All addresses saved for this client.</p>
              <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">#</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">Emirate</th>
                        <th className="min-w-[140px] px-4 py-3 font-semibold text-gray-700">Location</th>
                        <th className="min-w-[200px] px-4 py-3 font-semibold text-gray-700">Address</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">Contact</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">PO Box</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">Store No</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700">Default</th>
                        <th className="whitespace-nowrap px-4 py-3 font-semibold text-gray-700 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {activeAddresses.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                            No addresses yet. Use the form above to add the first address.
                          </td>
                        </tr>
                      ) : (
                        activeAddresses.map((address, displayIndex) => {
                          const globalIndex = client.ClientAddresses.findIndex((a) => a === address);
                          return (
                            <tr
                              key={`${globalIndex}-${displayIndex}`}
                              className={`align-top hover:bg-gray-50/80 ${
                                editingAddressIndex === globalIndex ? 'bg-[#f0f9f4]/90' : ''
                              }`}
                            >
                              <td className="whitespace-nowrap px-4 py-3 text-gray-600">{displayIndex + 1}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-900">{getEmirateName(address.EmirateId)}</td>
                              <td className="px-4 py-3 text-gray-900">{address.Location?.Name || '—'}</td>
                              <td className="max-w-xs px-4 py-3 text-gray-700">
                                <span className="line-clamp-3 whitespace-pre-wrap">{address.Address || '—'}</span>
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.ContactNo || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.POBOX || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-gray-700">{address.StoreNo || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3">
                                {address.IsDefault ? (
                                  <span className="inline-flex rounded-full bg-[#f0f9f4] px-2.5 py-0.5 text-xs font-medium text-[#46955A]">
                                    Default
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && setActiveAddressDefault(globalIndex)}
                                    className="text-xs font-medium text-[#46955A] hover:underline"
                                  >
                                    Set default
                                  </button>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right">
                                <div className="inline-flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && startEditAddress(globalIndex)}
                                    className="inline-flex items-center justify-center rounded-lg p-2 text-[#46955A] transition-colors hover:bg-[#f0f9f4]"
                                    title="Edit address"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => globalIndex >= 0 && removeAddress(globalIndex)}
                                    className="inline-flex items-center justify-center rounded-lg bg-red-50 p-2 text-red-600 transition-colors hover:bg-red-100"
                                    title="Remove address"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this client? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteWithConfirm}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddClient;
